/* Predictive search: live results inside the search overlay (sections/search-overlay.liquid).
   Results are rendered by sections/predictive-search.liquid through Shopify's Predictive Search API
   and injected here. Pressing Enter still submits the form to the full search page. */
if (!customElements.get('lmb-predictive-search')) {
  const SECTION_ID = 'predictive-search';
  const DEBOUNCE_MS = 250;
  const CACHE_SIZE = 30;

  const fetchDocument = async (url, signal) => {
    if (window.LMB && typeof window.LMB.fetchHTML === 'function') return window.LMB.fetchHTML(url, { signal });
    const response = await fetch(url, { credentials: 'same-origin', signal });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  class LmbPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="q"]');
      this.results = this.querySelector('[data-ps-results]');
      if (!this.input || !this.results) return;

      this.form = this.input.form;
      this.status = this.querySelector('[data-ps-status]');
      this.popular = this.querySelector('[data-ps-popular]');
      this.overlay = this.closest('lmb-overlay');
      this.live = this.dataset.live !== 'false';
      this.cache = this.cache || new Map();

      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onOverlayOpen = this.onOverlayOpen.bind(this);
      this.onOverlayClose = this.onOverlayClose.bind(this);

      this.input.addEventListener('input', this.onInput);
      this.addEventListener('keydown', this.onKeydown);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);
      if (this.overlay) {
        this.overlay.addEventListener('lmb:open', this.onOverlayOpen);
        this.overlay.addEventListener('lmb:close', this.onOverlayClose);
      }
      this.bound = true;
    }

    disconnectedCallback() {
      if (!this.bound) return;
      this.bound = false;
      this.cancel();
      clearTimeout(this.resetTimer);
      clearTimeout(this.announceTimer);
      this.input.removeEventListener('input', this.onInput);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.overlay) {
        this.overlay.removeEventListener('lmb:open', this.onOverlayOpen);
        this.overlay.removeEventListener('lmb:close', this.onOverlayClose);
      }
    }

    get query() {
      return this.input.value.trim();
    }

    onInput() {
      const query = this.query;
      if (this.popular) this.popular.hidden = query.length > 0;
      if (!this.live) return;

      this.cancel();
      if (!query) {
        this.clear();
        return;
      }
      if (this.results.childElementCount) this.results.classList.add('is-loading');
      this.results.setAttribute('aria-busy', 'true');
      this.timer = setTimeout(() => this.search(query), DEBOUNCE_MS);
    }

    onSubmit(event) {
      // An empty search would only open an empty results page.
      if (!this.query) {
        event.preventDefault();
        this.input.focus();
      }
    }

    async search(query) {
      const key = query.toLowerCase();
      if (this.cache.has(key)) {
        this.render(this.cache.get(key));
        return;
      }

      const controller = new AbortController();
      this.controller = controller;
      try {
        const doc = await fetchDocument(this.buildUrl(query), controller.signal);
        const section = doc.getElementById(`shopify-section-${SECTION_ID}`);
        const html = section ? section.innerHTML.trim() : '';
        this.remember(key, html);
        // Ignore answers to a query the shopper has already changed.
        if (this.query.toLowerCase() === key) this.render(html);
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Leave what is shown; Enter still opens the full search page.
        this.results.classList.remove('is-loading');
        this.results.removeAttribute('aria-busy');
      } finally {
        if (this.controller === controller) this.controller = null;
      }
    }

    buildUrl(query) {
      const url = new URL(this.dataset.url || '/search/suggest', window.location.origin);
      const params = url.searchParams;
      params.set('q', query);
      params.set('resources[type]', this.dataset.types || 'query,collection,product');
      params.set('resources[limit]', this.dataset.limit || '4');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', this.dataset.fields || 'title,product_type,variants.title,vendor');
      params.set('section_id', SECTION_ID);
      return url.toString();
    }

    remember(key, html) {
      this.cache.set(key, html);
      if (this.cache.size > CACHE_SIZE) this.cache.delete(this.cache.keys().next().value);
    }

    render(html) {
      this.results.classList.remove('is-loading');
      this.results.removeAttribute('aria-busy');
      if (!html) {
        this.clear();
        return;
      }
      this.results.innerHTML = html;

      const label = (this.dataset.viewAllLabel || '').trim();
      if (label) {
        this.results.querySelectorAll('[data-ps-view-all-label]').forEach((el) => {
          el.textContent = label;
        });
      }

      const summary = this.results.querySelector('[data-ps-summary]');
      this.announce(summary ? summary.textContent.trim() : '');
    }

    clear() {
      this.results.innerHTML = '';
      this.results.classList.remove('is-loading');
      this.results.removeAttribute('aria-busy');
      this.announce('');
    }

    cancel() {
      clearTimeout(this.timer);
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
    }

    announce(message) {
      if (!this.status) return;
      // Clear first so the same message ("4 results") is announced again for a new query.
      this.status.textContent = '';
      clearTimeout(this.announceTimer);
      if (message) {
        this.announceTimer = setTimeout(() => {
          this.status.textContent = message;
        }, 100);
      }
    }

    onOverlayOpen() {
      clearTimeout(this.resetTimer);
      // Focus inside the tap that opened the overlay, so phones show the keyboard.
      // (lmb-overlay focuses [autofocus] a frame later, which iOS may not treat as part of the tap.)
      if (!this.input.disabled) this.input.focus({ preventScroll: true });
    }

    onOverlayClose() {
      this.cancel();
      clearTimeout(this.resetTimer);
      // Wait for the closing transition so the panel doesn't collapse while it fades.
      this.resetTimer = setTimeout(() => {
        if (this.overlay && this.overlay.isOpen) return;
        this.input.value = '';
        if (this.popular) this.popular.hidden = false;
        this.clear();
      }, 350);
    }

    /* Arrow keys move between the search field and the visible result links. */
    items() {
      return Array.from(this.querySelectorAll('[data-ps-popular] a[href], [data-ps-results] a[href]')).filter(
        (el) => el.getClientRects().length > 0 && !el.closest('[hidden]')
      );
    }

    onKeydown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

      const active = document.activeElement;
      const fromInput = active === this.input;
      if (fromInput && event.key === 'ArrowUp') return;

      const items = this.items();
      if (!items.length) return;
      const index = items.indexOf(active);
      if (!fromInput && index === -1) return;

      event.preventDefault();
      if (event.key === 'ArrowDown') {
        const next = fromInput ? items[0] : items[Math.min(index + 1, items.length - 1)];
        next.focus();
      } else if (index === 0) {
        this.input.focus();
      } else {
        items[index - 1].focus();
      }
    }
  }

  customElements.define('lmb-predictive-search', LmbPredictiveSearch);

  /* Theme editor: show the overlay while its section is selected, and keep it open when the section re-renders. */
  const searchOverlayIn = (root) => (root && root.querySelector ? root.querySelector('lmb-overlay[data-search-overlay]') : null);
  let reopenSectionId = null;

  // Opening normally focuses the search field. In the editor that would pull focus away from the
  // setting the merchant is typing in (the section re-renders as they type), so the field is
  // disabled for the one frame in which lmb-overlay moves focus.
  const openInEditor = (overlay) => {
    if (!overlay || typeof overlay.open !== 'function' || overlay.isOpen) return;
    const input = overlay.querySelector('input[name="q"]');
    if (input) input.disabled = true;
    overlay.open();
    requestAnimationFrame(() => {
      if (input) input.disabled = false;
    });
  };

  document.addEventListener('shopify:section:select', (event) => {
    openInEditor(searchOverlayIn(event.target));
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const overlay = searchOverlayIn(event.target);
    if (overlay && typeof overlay.close === 'function') overlay.close({ restoreFocus: false });
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const overlay = searchOverlayIn(event.target);
    if (overlay && overlay.isOpen) {
      reopenSectionId = event.detail && event.detail.sectionId;
      overlay.close({ restoreFocus: false });
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (!reopenSectionId || !event.detail || event.detail.sectionId !== reopenSectionId) return;
    reopenSectionId = null;
    openInEditor(searchOverlayIn(event.target));
  });
}
